import React, { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  useMediaQuery,
  Theme,
  styled,
  ButtonProps,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";
import variables from "../../../assets/styles/variables.module.scss";
import CreateGroup from "../../Components/CreateGroup";
import { Group } from "../../../Shared-ui/Types/Group";
import { MultiAgent } from "../../../Shared-ui/Types/MultiAgent";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { ADMIN_GROUPS, TEAMS } from "../../../Services/Endpoint";
import CreateMultiAgent from "../../Components/CreateMultiAgent";
import { formatDate } from "../../../Shared-ui/utils/Common/Common";
import ConfirmBase from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";

const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
}));

interface Team {
  id: number;
  name: string;
  description: string;
  status: 'ACTIVE' | 'PAUSED';
  members: number;
  shared: 'Yes' | 'No';
}

interface MyComponentProps { }

const AdminGroups: React.FC<MyComponentProps> = () => {
  const dispatch = useDispatch()
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [usersCount, setUsersCount] = useState(0)
  const [confirmPopupData, setConfirmPopupData] = useState<ConfirmBase>({ show: false })
  const [groupCount, setGroupCount] = useState(0)

  const [groups, setGroups] = React.useState<Group[]>([]);
  const [group, setGroup] = React.useState<Group | null>(null);

  useEffect(() => {
    getGroups()
  }, [])


  const getGroups = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${ADMIN_GROUPS}?page=${pagination.page + 1}&page_size=${pagination.pageSize}`).then((res) => res?.data);
      data?.items?.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setGroups(data.items || []);
      setGroupCount(data.pagination.total || 0)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  }



  const handleEdit = (row: Group) => {
    setGroup(row)
    setOpen(true)
  };

  const deleteGroup = async (rowInfo: any) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading())
      const groups: Group = rowInfo.row
      const data = await BaseInstance.delete(`${ADMIN_GROUPS}/${groups.id}`,).then(res => res?.data)
      dispatch(ShowSuccess("group Deleted"))
      getGroups()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }


  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "is_public",
      headerName: "Public",
      width: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "member_count",
      headerName: "Members",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 150,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            {formatDate(params.value)}
          </Box>
        );
      },
    },
    {
      field: "updated_at",
      headerName: "Updated",
      width: 150,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            {formatDate(params.value || params.row.created_at)}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const handleDeleteClick = (event: React.MouseEvent) => {
          setConfirmPopupData({
            show: true,
            setConfirmPopupData: setConfirmPopupData,
            title: "Confirm Deletion",
            info: "Are you sure want to delete your data? This action cannot be undone.",
            varient: "delete",
            functionName: deleteGroup,
            functionParam: params
          })
        };
        return (
          <Box sx={{ display: "flex", gap: 1, marginTop: 1.5, justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Tooltip title="Edit" placement="top-start">
              <IconButton
                size="small"
                onClick={() => handleEdit(params.row)}
                sx={{ "&:hover": { color: "primary.main" } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" placement="top-start">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row)}
                sx={{ "&:hover": { color: "error.main" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    },
  ];


  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false)
    getGroups()
    setGroup(null);
  };

  const handlePaginationMetaChange = (paginationMeta: any) => {
    const { page, pageSize, rowCount } = paginationMeta;
    setPagination({ page: page, pageSize: pageSize })
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { md: "flex-end", sm: "flex-start" },
            gap: { md: "10px", sm: "0" },
            flexDirection: { md: "row", sm: "column", xs: "column" },
          }}
        >
          <Breadcrumb data={[{ name: "Teams", path: "" }]} />
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <ColorButton
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            size={isSmallScreen ? "small" : "large"}
            sx={{
              textTransform: "capitalize",
              fontWeight: "400",
              whiteSpace: "nowrap",
              borderRadius: { sm: "8px", xs: "4px" },
              height: "40px",
            }}
            onClick={handleOpen}
          >
            Add Team
          </ColorButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            height: "100%",
            gap: 4,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={{ height: "100%", width: "100%" }}>
              <DataGrid
                rows={groups}
                columns={columns}
                pageSizeOptions={[10]}
                rowCount={groupCount}
                initialState={{ pagination: { paginationModel: pagination } }}
                onPaginationModelChange={handlePaginationMetaChange}
                disableColumnResize // Disables resizing
                paginationMode="server"
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
      <CreateGroup open={open} handleClose={handleClose} group={group} />
      <ConfirmPopup details={confirmPopupData} />
    </Container>
  );
};

export default AdminGroups;